// src/personel-takip/src/pages/RaporlarPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection, doc, getDoc, getDocs, query, where,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";

/* ================= Helpers ================= */
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const TRUC = (s="") => String(s||"").toUpperCase();
const titleCaseTR = (s="") => (s||"").toLowerCase().split(" ").filter(Boolean).map(w=>w[0]?.toUpperCase()+w.slice(1)).join(" ");
const isSunday = (y,m,d) => new Date(y,m-1,d).getDay()===0;

const parseDotOrISO = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  const s = String(v).replaceAll("/", ".").replaceAll("-", ".");
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return new Date(+m[3], +m[2]-1, +m[1]);
  if (/^\d{4}\.\d{2}\.\d{2}$/.test(s)) { const [Y,M,D]=s.split(".").map(Number); return new Date(Y,M-1,D); }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return new Date(String(v));
  return null;
};
const getIntervals = (tarih={})=>{
  const g=[], c=[];
  Object.entries(tarih).forEach(([k,v])=>{
    if (!v) return;
    if (k.startsWith("giris")) g.push([k, parseDotOrISO(v)]);
    if (k.startsWith("cikis")) c.push([k, parseDotOrISO(v)]);
  });
  const num=(s)=>+(s.match(/\d+/)?.[0]||0);
  g.sort((a,b)=>num(a[0])-num(b[0]));
  c.sort((a,b)=>num(a[0])-num(b[0]));
  const out=[];
  for (let i=0;i<g.length;i++){ const s=g[i][1], e=c[i]?.[1]||null; if (s) out.push([s,e]); }
  return out;
};
const isActiveOnDate = (tarih, y,m,d)=>{
  const ivs = getIntervals(tarih||{});
  const dt = new Date(y,m-1,d);
  return ivs.some(([s,e])=>{
    const end = e ? new Date(e.getFullYear(), e.getMonth(), e.getDate()+1) : null;
    return s<=dt && (!end || end>dt);
  });
};

/* Basit HTML→Excel aktarımı */
const exportRowsToExcel = (rows, headers, filename="liste.xls")=>{
  const esc=(v)=>String(v??"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const thead="<tr>"+headers.map(h=>`<th>${esc(h)}</th>`).join("")+"</tr>";
  const tbody=rows.map(r=>"<tr>"+r.map(c=>`<td>${esc(c)}</td>`).join("")+"</tr>").join("");
  const html = `<html><head><meta charset='utf-8'></head><body><table border='1'>${thead}${tbody}</table></body></html>`;
  const blob = new Blob([html], {type:"application/vnd.ms-excel;charset=utf-8;"});
  const url = URL.createObjectURL(blob); const a=document.createElement("a");
  a.href=url; a.download=filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

/* Yardımcı: tarih aralığında tek tek gün dolaş */
const eachDate = (from, to, fn) => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  while (d <= to) { fn(d); d.setDate(d.getDate()+1); }
};

/* ================= Page ================= */
const RaporlarPage = () => {
  /* ---- Stil: Ayrı CSS yerine component içine enjekte ---- */
  const styled = useRef(false);
  useEffect(() => {
    if (styled.current) return;
    styled.current = true;
    const css = `
      .rapor-container{max-width:1180px;margin:10px auto 24px;padding:0 6px;}
      .rapor-container h2{margin:0 0 8px 0;}
      .chart-section{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:12px 14px;margin-top:12px;}
      .chart-section h3{margin:0 0 8px;}
      .muted{color:#6b7280;font-size:13px;}
      .table-scroll{overflow-x:auto;}
      table.top-table{width:100%;border-collapse:collapse;font-size:13px;}
      table.top-table th, table.top-table td{white-space:nowrap;text-align:left;padding:6px 8px;border-bottom:1px solid #e5e7eb;}
      .toolbar{display:flex;gap:8px;align-items:center;margin:6px 0 8px;}
      .btn{border:0;background:#111827;color:#fff;font-weight:600;padding:7px 12px;border-radius:8px;cursor:pointer;}
      .btn:hover{opacity:.95;}
    `;
    const style = document.createElement("style");
    style.setAttribute("data-raporlar-inline", "1");
    style.innerHTML = css;
    document.head.appendChild(style);
    return () => { try{ document.head.removeChild(style); }catch{} };
  }, []);

  /* DATA KAYNAKLARI */
  const me = auth?.currentUser?.email?.toLowerCase() || "";

  const [allowedTesis, setAllowedTesis] = useState([]);
  const [persons, setPersons] = useState([]);
  const [puantajIndex, setPuantajIndex] = useState({});
  const [holidaysByYear, setHolidaysByYear] = useState({});
  const [docTypes, setDocTypes] = useState([]);
  const [docs, setDocs] = useState([]);

  /* — Yetkili olduğu tesisler — (kullanıcıya göre) */
  useEffect(()=>{(async()=>{
    try{
      if (!me){ setAllowedTesis([]); return; }
      const sP = await getDocs(query(collection(db,"personeller"), where("email","==", me)));
      const own = new Set();
      sP.forEach(d=>{
        const v=d.data()||{};
        if (v.tesis) own.add(TRUC(v.tesis));
        let extra=v.gorebilecegiTesisler;
        if (typeof extra==="string") extra=extra.split(",").map(x=>x.trim()).filter(Boolean);
        (extra||[]).forEach(t=>own.add(TRUC(t)));
      });
      setAllowedTesis(Array.from(own));
    } catch { setAllowedTesis([]); }
  })();},[me]);

  /* — Personeller — (sadece yetkili tesisler) */
  useEffect(()=>{(async()=>{
    const snap = await getDocs(collection(db,"personeller"));
    let arr = snap.docs.map(d=>({ id:d.id, ...d.data() }));
    const setT = new Set(allowedTesis);
    arr = arr.filter(p=>setT.has(TRUC(p.tesis||"")));
    setPersons(arr);
  })();},[allowedTesis]);

  /* Bugün aktif olan personel */
  const today = new Date();
  const personsActive = useMemo(()=>{
    const y=today.getFullYear(), m=today.getMonth()+1, d=today.getDate();
    return persons.filter(p=>isActiveOnDate(p.tarih||{}, y,m,d));
  },[persons]);

  /* Aktif tesis seti (aktif çalışanı olmayan tesisleri tamamen gizle) */
  const tesisWithActive = useMemo(()=>{
    const set=new Set(); personsActive.forEach(p=>{ if (p.tesis) set.add(TRUC(p.tesis)); }); return set;
  },[personsActive]);

  const personsFiltered = useMemo(()=>{
    return personsActive.filter(p=>tesisWithActive.has(TRUC(p.tesis||"")));
  },[personsActive, tesisWithActive]);

  /* — Puantaj Havuzu (tüm yıllar) — */
  useEffect(()=>{(async()=>{
    const qref = query(collection(db,"puantajV2"), where("yyyymm",">=","200001"), where("yyyymm","<=","210012"));
    const snap = await getDocs(qref);
    const byPid={};
    snap.forEach(d=>{
      const v=d.data()||{};
      const { personelId, date, status, enteredAt, enteredBy, note } = v;
      if (!personelId || !date) return;
      if (!byPid[personelId]) byPid[personelId]={};
      byPid[personelId][date]={ status, enteredAt, enteredBy, note };
    });
    setPuantajIndex(byPid);
  })();},[]);

  /* =============== HAK/KULLANIM grafiği için resmi tatiller =============== */
  // En erken aktif başlangıcı bul → o yıldan bugüne tatilleri çek
  const minActiveStart = useMemo(()=>{
    let min = null;
    for (const p of personsFiltered){
      const ivs = getIntervals(p.tarih||{});
      for (const [s] of ivs){
        if (!s) continue;
        if (!min || s < min) min = s;
      }
    }
    return min || new Date(today.getFullYear(), 0, 1);
  },[personsFiltered]);

  const yearsForHolidays = useMemo(()=>{
    const ys=[]; for(let y=minActiveStart.getFullYear(); y<=today.getFullYear(); y++) ys.push(y); return ys;
  },[minActiveStart]);

  useEffect(()=>{(async()=>{
    const next={};
    for (const y of yearsForHolidays){
      const g = await getDoc(doc(db,"resmiTatil", String(y)));
      next[y] = {
        days: g.exists()? (g.data().days||[]) : [],
        apply: g.exists()? (g.data().apply||{}) : {}
      };
    }
    setHolidaysByYear(next);
  })();},[yearsForHolidays.join("|")]);

  /* ================= 1) Tesis Bazında Hak (Pazar+T) vs Kullanım (İ+T) ================= */
  const siteAgg = useMemo(()=>{
    const map={};
    const getHolidaySets=(y)=>{ const h=holidaysByYear[y]||{days:[],apply:{}}; return { list:new Set(h.days||[]), apply:h.apply||{} }; };

    const to = today;
    for (const p of personsFiltered){
      const tesis = TRUC(p.tesis||"?");
      if (!map[tesis]) map[tesis] = { hak:0, used:0 };

      const ivs = getIntervals(p.tarih||{});
      for (const [s,e] of ivs){
        const from = new Date(s.getFullYear(), s.getMonth(), s.getDate());
        const until = e ? new Date(e.getFullYear(), e.getMonth(), e.getDate()) : to;

        eachDate(from, until, (dt)=>{
          const y = dt.getFullYear(), m = dt.getMonth()+1, d = dt.getDate();
          const iso = toISO(y,m,d);
          // Kullanım
          const st = (puantajIndex[p.id]?.[iso]?.status) ??
                     ((holidaysByYear[y]?.apply||{})[iso] ? "RESMI_TATIL" : "CALISMA");
          if (st==="IZIN" || st==="RESMI_TATIL") map[tesis].used++;

          // Hak
          if (isSunday(y,m,d)) {
            map[tesis].hak++;
          } else {
            const hs = getHolidaySets(y);
            if (hs.list.has(iso) && !(st==="IZIN"||st==="UCRETSIZ"||st==="RAPOR"||st==="YILLIK")) {
              map[tesis].hak++;
            }
          }
        });
      }
    }
    return map;
  },[personsFiltered, puantajIndex, holidaysByYear]);

  const izinChartData = useMemo(()=>{
    return Object.entries(siteAgg)
      .filter(([t])=>tesisWithActive.has(t))
      .sort(([a],[b])=>a.localeCompare(b,"tr",{sensitivity:"base"}))
      .map(([t,v])=>({ tesis:t, hak:v.hak, kullanim:v.used }));
  },[siteAgg, tesisWithActive]);

  const exportIzinTable = ()=>{
    const headers=["Tesis","Hak (Pazar+T)","Kullanım (İ+T)","Kullanım %"];
    const rows=izinChartData.map(r=>[r.tesis, r.hak, r.kullanim, r.hak? ((r.kullanim/r.hak*100).toFixed(1)+"%"):"-"]);
    exportRowsToExcel(rows, headers, "tesis-izin-ozet.xls");
  };

  /* ================= 2) Eksik Evraklar (Aktif Personel) ================= */
  // Evrak tipleri & dokümanlar
  useEffect(()=>{(async()=>{
    const [tSnap, dSnap] = await Promise.all([
      getDocs(collection(db,"personelDocTypes")),
      getDocs(collection(db,"personelDocs")),
    ]);
    setDocTypes(tSnap.docs.map(d=>({ id:d.id, ...d.data() })));
    setDocs(dSnap.docs.map(d=>({ id:d.id, ...d.data() })));
  })();},[]);

  const evrakSummary = useMemo(()=>{
    // Aktif personel id seti
    const activeIds = new Set(personsFiltered.map(p=>p.id));

    // Rol -> zorunlu evrak
    const roleReq = {};
    docTypes.forEach(t=>{
      const reqRoles = (t.rolesRequired || []).map(TRUC);
      reqRoles.forEach(r=>{
        if (!roleReq[r]) roleReq[r] = new Set();
        roleReq[r].add(t.id); // t.id = docTypeId
      });
    });

    // Hangi personelde hangi doküman var?
    const hasByPid = {};
    docs.forEach(d=>{
      if (!activeIds.has(d.personelId)) return;
      if (!hasByPid[d.personelId]) hasByPid[d.personelId] = new Set();
      hasByPid[d.personelId].add(d.docTypeId);
    });

    // Toplam gereklilik ve var sayımı
    const nameByType = {};
    docTypes.forEach(t=>{ nameByType[t.id] = t.name || t.id; });

    const agg = {};
    for (const p of personsFiltered){
      const roleUC = TRUC(p.gorev || "");
      const required = Array.from(roleReq[roleUC] || []); // bu role için zorunlu docTypeId listesi
      const haveSet = hasByPid[p.id] || new Set();
      required.forEach(dtId=>{
        if (!agg[dtId]) agg[dtId] = { name: nameByType[dtId] || dtId, total: 0, have: 0 };
        agg[dtId].total += 1;            // bu evrak bu kişi için zorunlu
        if (haveSet.has(dtId)) agg[dtId].have += 1;  // kişi bu evrağı yüklemiş
      });
    }

    // Sıralama: en çok eksik olandan
    const arr = Object.values(agg).map(x=>({ ...x, missing: x.total - x.have }));
    arr.sort((a,b)=> (b.missing - a.missing) || a.name.localeCompare(b.name, "tr", {sensitivity:"base"}));
    return arr;
  },[personsFiltered, docTypes, docs]);

  const evrakChartData = useMemo(()=>{
    return evrakSummary.map(d=>({ evrak: d.name, toplam: d.total, var: d.have, eksik: d.missing }));
  },[evrakSummary]);

  const exportEvrakTable = ()=>{
    const headers=["Evrak Tipi","Toplam Gereklilik","Sistemde Var","Eksik","Tamamlanma %"];
    const rows=evrakChartData.map(r=>[r.evrak, r.toplam, r.var, r.eksik, r.toplam? ((r.var/r.toplam*100).toFixed(1)+"%"):"-"]);
    exportRowsToExcel(rows, headers, "eksik-evrak-ozet.xls");
  };

  /* ================= 3) Takdir–Uyarı Skoru (Teşekkür/Prim/Uyarı/Tutanak) ================= */
  // Kaynak: personelKayitlar, tip: PRIM / TESKKUR / UYARI / TUTANAK, isDeleted => sayma
  const [kayitlar, setKayitlar] = useState([]);
  useEffect(()=>{(async()=>{
    try{
      const s = await getDocs(collection(db,"personelKayitlar"));
      const rows = s.docs.map(d=>({ id:d.id, ...(d.data()||{}) }));
      setKayitlar(rows);
    }catch{ setKayitlar([]); }
  })()},[]);

  const W = { TESKKUR: 1.0, PRIM: 1.5, UYARI: -1.0, TUTANAK: -2.0 };

  const praiseRows = useMemo(()=>{
    const activeIds = new Set(personsFiltered.map(p=>p.id));
    const byPid = {};

    for (const r of kayitlar){
      if (r.isDeleted) continue;                   // soft-silinenleri atla
      const pid = r.personelId;
      if (!pid || !activeIds.has(pid)) continue;

      const t = String(r.tip||"").toUpperCase();   // PRIM / TESKKUR / UYARI / TUTANAK
      if (!byPid[pid]) byPid[pid] = { tesekkur:0, prim:0, uyari:0, tutanak:0 };
      if (t==="TESKKUR") byPid[pid].tesekkur++;
      else if (t==="PRIM") byPid[pid].prim++;
      else if (t==="UYARI") byPid[pid].uyari++;
      else if (t==="TUTANAK") byPid[pid].tutanak++;
    }

    const rows = [];
    for (const p of personsFiltered){
      const c = byPid[p.id] || { tesekkur:0, prim:0, uyari:0, tutanak:0 };
      const skor = c.tesekkur*W.TESKKUR + c.prim*W.PRIM + c.uyari*W.UYARI + c.tutanak*W.TUTANAK;
      const adSoyad = `${titleCaseTR(p.ad||"")} ${TRUC(p.soyad||"")}`.trim();
      rows.push({
        pid: p.id,
        tesis: TRUC(p.tesis||"?"),
        adSoyad,
        ...c,
        skor,
      });
    }

    // Sıfır kayıtlı olanları gösterme
    const filtered = rows.filter(r => (r.tesekkur + r.prim + r.uyari + r.tutanak) > 0);

    // En iyi → en kötü
    filtered.sort((a,b)=> (b.skor - a.skor) || a.adSoyad.localeCompare(b.adSoyad,"tr",{sensitivity:"base"}));
    return filtered;
  },[personsFiltered, kayitlar]);

  const praiseChartData = useMemo(()=>{
    return praiseRows.map(r=>({
      personel: r.adSoyad,
      skor: r.skor,
      fill: r.skor < 0 ? "#ef4444" : "#60a5fa"
    }));
  },[praiseRows]);

  const exportPraise = ()=>{
    const headers=["Tesis","Personel","Teşekkür","Prim","Uyarı","Tutanak","Skor"];
    const rows = praiseRows.map(r=>[r.tesis, r.adSoyad, r.tesekkur, r.prim, r.uyari, r.tutanak, r.skor.toFixed(2)]);
    exportRowsToExcel(rows, headers, "takdir-uyari-skoru.xls");
  };

  /* ================= Render ================= */
  return (
    <div className="rapor-container">
      <h2>📊 Raporlar</h2>

      {/* 1) Tesis Bazında Hak vs Kullanım — (Hak arka, Kullanım içte) + tablo */}
      <div className="chart-section">
        <div className="toolbar">
          <h3 style={{margin:0}}>Tesis Bazında Hak (Pazar+T) vs Kullanım (İ+T)</h3>
          <button className="btn" onClick={exportIzinTable}>Excel’e Aktar</button>
        </div>
        {izinChartData.length === 0 ? (
          <p className="muted">Aktif personeli olan tesis bulunamadı.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={izinChartData} barCategoryGap={-10}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tesis" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                {/* Arka plan: HAK (geniş bar) */}
                <Bar dataKey="hak" name="Hak" fill="#e8f3ff" stroke="#cfe2ff" barSize={30} radius={[10,10,0,0]} />
                {/* İçte: KULLANIM (dar bar) */}
                <Bar dataKey="kullanim" name="Kullanım" fill="#8ab6ff" barSize={18} radius={[10,10,0,0]} />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="table-scroll" style={{marginTop:8}}>
              <table className="top-table">
                <thead>
                  <tr>
                    <th>Tesis</th>
                    <th>Hak (Pazar+T)</th>
                    <th>Kullanım (İ+T)</th>
                    <th>Kullanım %</th>
                  </tr>
                </thead>
                <tbody>
                  {izinChartData.map((r,i)=>(
                    <tr key={i}>
                      <td>{r.tesis}</td>
                      <td>{r.hak}</td>
                      <td>{r.kullanim}</td>
                      <td>{r.hak ? ( (r.kullanim/r.hak)*100 ).toFixed(1)+"%" : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* 2) Eksik Evraklar — (Toplam arka, Var içte) + tablo */}
      <div className="chart-section">
        <div className="toolbar">
          <h3 style={{margin:0}}>Eksik Evraklar (Aktif Personel)</h3>
          <button className="btn" onClick={exportEvrakTable}>Excel’e Aktar</button>
        </div>
        {evrakChartData.length === 0 ? (
          <p className="muted">Görüntülenecek zorunlu evrak bulunamadı.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={evrakChartData} barCategoryGap={-10}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="evrak" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                {/* Arka plan: Toplam gereklilik */}
                <Bar dataKey="toplam" name="Toplam Gereklilik" fill="#dcfce7" stroke="#bbf7d0" barSize={30} radius={[10,10,0,0]} />
                {/* İçte: Sistemde Var */}
                <Bar dataKey="var" name="Sistemde Var" fill="#86efac" barSize={18} radius={[10,10,0,0]} />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="table-scroll" style={{marginTop:8}}>
              <table className="top-table">
                <thead>
                  <tr>
                    <th>Evrak Tipi</th>
                    <th>Toplam Gereklilik</th>
                    <th>Sistemde Var</th>
                    <th>Eksik</th>
                    <th>Tamamlanma %</th>
                  </tr>
                </thead>
                <tbody>
                  {evrakChartData.map((r,i)=>(
                    <tr key={i}>
                      <td>{r.evrak}</td>
                      <td>{r.toplam}</td>
                      <td>{r.var}</td>
                      <td>{r.eksik}</td>
                      <td>{r.toplam ? ((r.var/r.toplam)*100).toFixed(1)+"%" : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* 3) Takdir–Uyarı Skoru — grafik + tablo aynı alan */}
      <div className="chart-section">
        <div className="toolbar">
          <h3 style={{margin:0}}>Takdir–Uyarı Skoru (Teşekkür/Prim/Uyarı/Tutanak)</h3>
          <button className="btn" onClick={exportPraise}>Excel’e Aktar</button>
        </div>

        {praiseRows.length === 0 ? (
          <p className="muted">Kayıt bulunamadı.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={praiseChartData} layout="vertical" barCategoryGap={8}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="personel" width={160} />
                <Tooltip />
                <Legend />
                <Bar dataKey="skor" name="Skor" barSize={16} radius={[0,10,10,0]}>
                  {praiseChartData.map((d,i)=>(
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>

            <div className="table-scroll" style={{marginTop:8}}>
              <table className="top-table">
                <thead>
                  <tr>
                    <th>Tesis</th>
                    <th>Personel</th>
                    <th>Teşekkür</th>
                    <th>Prim</th>
                    <th>Uyarı</th>
                    <th>Tutanak</th>
                    <th>Skor</th>
                  </tr>
                </thead>
                <tbody>
                  {praiseRows.map((r,i)=>(
                    <tr key={i} style={{ background: r.skor<0 ? "#fee2e2" : "transparent" }}>
                      <td>{r.tesis}</td>
                      <td>{r.adSoyad}</td>
                      <td>{r.tesekkur}</td>
                      <td>{r.prim}</td>
                      <td>{r.uyari}</td>
                      <td>{r.tutanak}</td>
                      <td style={{ color: r.skor<0 ? "#b91c1c" : "#0f172a", fontWeight: 700 }}>
                        {r.skor.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RaporlarPage;
